// @ts-nocheck
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../components/ui/badge';
import type {
  CampaignType,
  DeploymentItemStatus,
  DeploymentStatus,
} from '../../../api/deploymentsApi';

// Class names are kept in static maps; the visible labels are resolved through
// literal translation calls in a switch — never a dynamic member-expression key —
// so the data/UI boundary holds and the static i18n guard passes.
const DEPLOYMENT_STATUS_CLASS: Record<DeploymentStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  completed_with_errors: 'bg-orange-100 text-orange-700',
  cancelled: 'bg-red-100 text-red-700',
};

const ITEM_STATUS_CLASS: Record<DeploymentItemStatus, string> = {
  pending: 'bg-gray-100 text-gray-700',
  created: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  skipped: 'bg-yellow-100 text-yellow-700',
};

const CAMPAIGN_TYPE_CLASS: Record<CampaignType, string> = {
  scheduled_integration: 'bg-indigo-100 text-indigo-700',
  permanent_maintenance: 'bg-teal-100 text-teal-700',
};

const deploymentStatusLabel = (t, status: DeploymentStatus): string => {
  switch (status) {
    case 'draft':
      return t('Draft');
    case 'in_progress':
      return t('In progress');
    case 'completed':
      return t('Completed');
    case 'completed_with_errors':
      return t('Completed with errors');
    case 'cancelled':
      return t('Cancelled');
    default:
      return String(status);
  }
};

const itemStatusLabel = (t, status: DeploymentItemStatus): string => {
  switch (status) {
    case 'pending':
      return t('Pending');
    case 'created':
      return t('Created item');
    case 'failed':
      return t('Failed');
    case 'skipped':
      return t('Skipped');
    default:
      return String(status);
  }
};

export const campaignTypeLabel = (t, type: CampaignType): string => {
  switch (type) {
    case 'scheduled_integration':
      return t('Scheduled integration');
    case 'permanent_maintenance':
      return t('Permanent maintenance');
    default:
      return String(type);
  }
};

export const DeploymentStatusBadge = ({ status }: { status: DeploymentStatus }) => {
  const { t } = useTranslation();
  const className = DEPLOYMENT_STATUS_CLASS[status] ?? 'bg-gray-100';
  return <Badge className={className}>{deploymentStatusLabel(t, status)}</Badge>;
};

export const DeploymentItemStatusBadge = ({
  status,
}: {
  status: DeploymentItemStatus;
}) => {
  const { t } = useTranslation();
  const className = ITEM_STATUS_CLASS[status] ?? 'bg-gray-100';
  return <Badge className={className}>{itemStatusLabel(t, status)}</Badge>;
};

export const CampaignTypeBadge = ({ type }: { type: CampaignType }) => {
  const { t } = useTranslation();
  const className = CAMPAIGN_TYPE_CLASS[type] ?? 'bg-gray-100';
  return <Badge className={className}>{campaignTypeLabel(t, type)}</Badge>;
};
